/**
 * The 3-D Secure 2 device fingerprint MultiSafepay sends to the card scheme as `customer.browser`.
 *
 * With the hosted payment page MSP reads this from the guest's browser itself. With the embedded
 * Payment Component the order is created server-to-server, so the page has to collect it and hand
 * it to our API — otherwise MSP cannot initiate 3-D Secure and declines the order immediately with
 * result code 3100 ("3DS Authentication Failed / Initiation failed"). Bancontact is always
 * 3-D Secure, so without this every Bancontact payment fails.
 *
 * Docs: https://docs.multisafepay.com/docs/3ds2
 */
export interface CheckoutBrowserInfo {
  javascript_enabled: boolean;
  java_enabled: boolean;
  cookies_enabled: boolean;
  language: string;
  screen_color_depth: number;
  screen_height: number;
  screen_width: number;
  /** Minutes behind UTC, as `Date.prototype.getTimezoneOffset()` reports it. */
  time_zone: number;
  platform: string;
  user_agent: string;
  /** EMV 3DS browserAcceptHeader — required to initiate 3-D Secure (MSP result 3100). */
  accept_header: string;
}

/**
 * The only bit depths EMV 3-D Secure accepts for `browserColorDepth`. Chrome on a wide-gamut Mac
 * display reports 30, which is not one of them: the authentication request is then invalid, 3DS
 * never starts, and MultiSafepay declines the order on creation (error 1024, reason 5999
 * "Internal System Error") for every card, its own test cards included. EMVCo's guidance is to
 * send the closest lower supported value.
 */
const EMV_3DS_COLOR_DEPTHS = [1, 4, 8, 15, 16, 24, 32, 48];

function toEmv3dsColorDepth(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 24;
  const supported = EMV_3DS_COLOR_DEPTHS.filter((depth) => depth <= value);
  return supported.length > 0 ? supported[supported.length - 1] : 24;
}

/**
 * Read straight off `navigator`/`screen` at submit time. Everything here is best-effort: an
 * embedded or hardened browser may not expose a field, and a partial fingerprint still beats none.
 */
export function collectBrowserInfo(): CheckoutBrowserInfo | undefined {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return undefined;

  let javaEnabled = false;
  try {
    // Removed in modern browsers; the scheme still expects the flag.
    javaEnabled = typeof navigator.javaEnabled === 'function' ? navigator.javaEnabled() : false;
  } catch {
    javaEnabled = false;
  }

  const screen = window.screen;

  return {
    javascript_enabled: true,
    java_enabled: javaEnabled,
    cookies_enabled: navigator.cookieEnabled === true,
    language: navigator.language || 'en-US',
    screen_color_depth: toEmv3dsColorDepth(screen?.colorDepth),
    screen_height: screen?.height ?? window.innerHeight,
    screen_width: screen?.width ?? window.innerWidth,
    time_zone: new Date().getTimezoneOffset(),
    // `navigator.platform` is deprecated but is what the 3DS spec asks for; UA-CH is the fallback.
    platform:
      (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData?.platform ||
      navigator.platform ||
      '',
    user_agent: navigator.userAgent || '',
    // JS cannot read the real HTTP Accept header; MSP's own examples use `*`.
    accept_header: '*/*',
  };
}
